import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FaCrown } from "react-icons/fa";

import { GameGridSkeleton } from "@/components/Casino";
import EmptyState from "@/components/Core/EmptyState";
import { useTranslate } from "@/hooks";
import { Game } from "@/types";
import { handleError } from "@/utils";

import { GameList, gameKey } from "./GameList";
import {
  DEFAULT_GAME_LIMIT,
  INFINITE_SCROLL_ROOT_MARGIN,
  LOAD_MORE_SKELETON_COUNT,
  SNAPSHOT_LIMIT,
  SNAPSHOT_TTL_MS,
} from "./data";
import { InfiniteGameListProps } from "./type";

interface LoadedGames {
  items: Game[];
  page: number;
  total: number;
}

const NOTHING_LOADED: LoadedGames = { items: [], page: 1, total: 0 };

/** What each list had loaded, by reset key, so Back returns to the same long list. */
const snapshots = new Map<string, LoadedGames & { at: number }>();

const rememberSnapshot = (key: string, list: LoadedGames) => {
  snapshots.delete(key);
  snapshots.set(key, { ...list, at: Date.now() });
  if (snapshots.size > SNAPSHOT_LIMIT) snapshots.delete(snapshots.keys().next().value as string);
};

export const InfiniteGameList: FC<InfiniteGameListProps> = ({
  loader,
  resetKey,
  limit = DEFAULT_GAME_LIMIT,
  onTotalChange,
  empty,
  remember = false,
  restore = false,
}) => {
  const t = useTranslate();
  const [list, setList] = useState<LoadedGames>(NOTHING_LOADED);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [failed, setFailed] = useState<"first" | "more" | null>(null);
  const [attempt, setAttempt] = useState(0);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const busyRef = useRef<boolean>(false);
  // Bumped by every reset: an answer to an older request is dropped, not added to the new list.
  const generationRef = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const onTotalRef = useRef(onTotalChange);
  onTotalRef.current = onTotalChange;

  const { items, page, total } = list;
  const hasMore = page * limit < total;

  useEffect(() => {
    const generation = ++generationRef.current;
    setFailed(null);
    setLoadingMore(false);
    onTotalRef.current?.(null);

    const snapshot = restore && remember ? snapshots.get(resetKey) : undefined;
    if (snapshot && Date.now() - snapshot.at < SNAPSHOT_TTL_MS) {
      busyRef.current = false;
      setList(snapshot);
      setLoading(false);
      onTotalRef.current?.(snapshot.total);
      return;
    }

    busyRef.current = true;
    setLoading(true);

    // The old games stay up, dimmed, until the new ones arrive: no flash of skeletons per filter.
    loaderRef
      .current(1, limit)
      .then((res) => {
        if (generation !== generationRef.current) return;
        setList({ items: res.results, page: 1, total: res.total });
        onTotalRef.current?.(res.total);
      })
      .catch((error) => {
        if (generation !== generationRef.current) return;
        handleError(error);
        setList(NOTHING_LOADED);
        setFailed("first");
      })
      .finally(() => {
        if (generation !== generationRef.current) return;
        setLoading(false);
        busyRef.current = false;
      });
  }, [resetKey, limit, attempt]);

  useEffect(() => {
    if (remember && items.length) rememberSnapshot(resetKey, list);
  }, [list]);

  const loadMore = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const generation = generationRef.current;
    setFailed(null);
    setLoadingMore(true);
    const next = page + 1;
    try {
      const res = await loaderRef.current(next, limit);
      if (generation !== generationRef.current) return;
      // A game can shift pages when the order changes between requests.
      setList((prev) => {
        const seen = new Set(prev.items.map(gameKey));
        const fresh = res.results.filter((game) => !seen.has(gameKey(game)));
        return { items: [...prev.items, ...fresh], page: next, total: res.total };
      });
    } catch (error) {
      if (generation !== generationRef.current) return;
      handleError(error);
      setFailed("more");
    } finally {
      if (generation === generationRef.current) {
        setLoadingMore(false);
        busyRef.current = false;
      }
    }
  }, [page, limit]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || !hasMore || failed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: INFINITE_SCROLL_ROOT_MARGIN },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, hasMore, loadMore, failed]);

  if (failed === "first") {
    return (
      <EmptyState
        title="game.list.load-failed"
        action={
          <Button
            variant="outlined"
            onClick={() => setAttempt((value) => value + 1)}
            sx={{ borderRadius: 999 }}
          >
            {t("common.retry")}
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={2.5}>
      <Box
        aria-busy={loading}
        sx={{
          opacity: loading && items.length > 0 ? 0.5 : 1,
          transition: "opacity 160ms ease",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <GameList gameItems={items} loading={loading && items.length === 0} empty={empty} />
      </Box>
      <div ref={sentinelRef} />
      {/* Cards rather than a spinner: the page keeps its shape as the next games arrive. */}
      {loadingMore && <GameGridSkeleton count={LOAD_MORE_SKELETON_COUNT} />}
      {failed === "more" && (
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1.5} py={2}>
          <Typography variant="body2" color="text.secondary">
            {t("game.list.load-more-failed")}
          </Typography>
          <Button variant="outlined" size="small" onClick={loadMore} sx={{ borderRadius: 999 }}>
            {t("common.retry")}
          </Button>
        </Stack>
      )}
      {/* The end of a list that took scrolling to reach, marked with the
          crown: without it, a list that stops loading looks like one that
          stalled. A list that fits one page does not need telling. */}
      {!loading && !hasMore && items.length > limit && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 2,
            color: "text.secondary",
            "& svg": { color: "#FDC445", fontSize: 18 },
          }}
        >
          <FaCrown aria-hidden />
          <Typography variant="body2">{t("game.list.end", { count: total })}</Typography>
        </Stack>
      )}
    </Stack>
  );
};
